import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { REACT_APP_API_URL } from "../../../../../env";

const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const columns = [
    {
        name: "Booking ID",
        selector: (row) => row.intBookingID,
        sortable: true,
        width: "120px",
    },
    {
        name: "Booking Ref No",
        selector: (row) => row.vchBookingRefNo,
        sortable: true,
        width: "180px",
    },
    {
        name: "Old Check-in",
        selector: (row) => formatDate(row.OldCheckIn),
        sortable: true,
        width: "140px",
    },
    {
        name: "New Check-in",
        selector: (row) => formatDate(row.NewCheckIn),
        sortable: true,
        width: "140px",
    },
    {
        name: "Old Check-out",
        selector: (row) => formatDate(row.OldCheckOut),
        sortable: true,
        width: "150px",
    },
    {
        name: "New Check-out",
        selector: (row) => formatDate(row.NewCheckOut),
        sortable: true,
        width: "150px",
    },
    {
        name: "Initial Booking Amount",
        selector: (row) => row.InitialBookingAmount ? `₹${row.InitialBookingAmount.toFixed(2)}` : "-",
        sortable: true,
        width: "180px",
        style: {
            justifyContent: 'flex-end',
        },
    },
    {
        name: "New Booking Amount",
        selector: (row) => row.NewBookingAmount ? `₹${row.NewBookingAmount.toFixed(2)}` : "-",
        sortable: true,
        width: "180px",
        style: {
            justifyContent: 'flex-end',
        },
    },
    {
        name: "Total Refund Amount",
        selector: (row) => row.TotalRefundAmount ? `₹${row.TotalRefundAmount.toFixed(2)}` : "-",
        sortable: true,
        width: "180px",
        style: {
            justifyContent: 'flex-end',
        },
    },
    {
        name: "Deduction %",
        selector: (row) => row.DeductionPercent ? `${row.DeductionPercent}%` : "-",
        sortable: true,
        width: "130px",
        style: {
            justifyContent: 'flex-end',
        },
    },
    {
        name: "Total Deduction Amount",
        selector: (row) => row.TotalDeductionAmount ? `₹${row.TotalDeductionAmount.toFixed(2)}` : "-",
        sortable: true,
        width: "190px",
        style: {
            justifyContent: 'flex-end',
        },
    },
    {
        name: "Last Modified On",
        selector: (row) => formatDateTime(row.LastModifiedOn),
        sortable: true,
        width: "180px",
    },
];


const customStyles = {
    rows: {
        style: {
            minHeight: "45px",
        },
    },
    headCells: {
        style: {
            fontWeight: "bold",
            fontSize: "13px",
            backgroundColor: "#f8f9fa",
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            paddingLeft: "12px",
            paddingRight: "12px",
            minWidth: "auto",
            wordBreak: "keep-all",
        },
    },
    headRow: {
        style: {
            backgroundColor: "#f8f9fa",
            minWidth: "100%",
        },
    },
    cells: {
        style: {
            border: "1px solid #dee2e6",
        },
    },
};

const PartialDayCancellation = () => {
    const [data, setData] = useState([]);
    const [pending, setPending] = useState(true);
    const [error, setError] = useState(null);
    const [dateError, setDateError] = useState("");

    // Filters state
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [searchBookingId, setSearchBookingId] = useState("");

    useEffect(() => {
        fetch(`${REACT_APP_API_URL}api/dashboard/admin/hotel-day-cancellation`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch data");
                return res.json();
            })
            .then((result) => {
                if (result.success) {
                    setData(result.data);
                } else {
                    setError("No data received");
                }
            })
            .catch((err) => setError(err.message))
            .finally(() => setPending(false));
    }, []);

    const filteredData = useMemo(() => {
        return data.filter((row) => {
            // Search by Booking ID or Booking Ref No
            if (searchBookingId) {
                const searchLower = searchBookingId.toLowerCase();
                const bookingIdMatch = row.intBookingID.toString().includes(searchBookingId);
                const bookingRefMatch = row.vchBookingRefNo &&
                    row.vchBookingRefNo.toLowerCase().includes(searchLower);

                if (!bookingIdMatch && !bookingRefMatch) {
                    return false;
                }
            }

            if (filterFrom) {
                const fromDate = new Date(filterFrom);
                const checkinDate = new Date(row.OldCheckIn);
                if (checkinDate < fromDate) return false;
            }
            if (filterTo) {
                const toDate = new Date(filterTo);
                const checkinDate = new Date(row.OldCheckIn);
                if (checkinDate > toDate) return false;
            }
            return true;
        });
    }, [data, filterFrom, filterTo, searchBookingId]);

    // Excel export function
    const exportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "PartialDayCancellation");
        XLSX.writeFile(wb, "PartialDayCancellation.xlsx");
    };



    const handleFromDateChange = (e) => {
        const value = e.target.value;
        setFilterFrom(value);
        if (filterTo && value && new Date(value) > new Date(filterTo)) {
            setDateError("From date cannot be after To date.");
        } else {
            setDateError("");
        }
    };

    const handleToDateChange = (e) => {
        const value = e.target.value;
        setFilterTo(value);
        if (filterFrom && value && new Date(filterFrom) > new Date(value)) {
            setDateError("From date cannot be after To date.");
        } else {
            setDateError("");
        }
    };

    return (
        <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
            <div className="row g-3 mb-3">
                <div className="col-12">
                    <h2 className="mb-3">Partial Day Cancellation List</h2>

                    <div className="card shadow-sm rounded-1 p-3">

                        <div className="d-flex flex-wrap gap-3 align-items-end p-3 border rounded mb-3">
                            <div className="d-flex flex-column">
                                <label className="form-label mb-1">From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filterFrom}
                                    onChange={handleFromDateChange}
                                    max={filterTo || undefined}
                                />
                            </div>
                            <div className="d-flex flex-column">
                                <label className="form-label mb-1">To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={filterTo}
                                    onChange={handleToDateChange}
                                    min={filterFrom || undefined}
                                />
                            </div>
                            {/* Search Booking ID/booking ref */}
                            <div className="d-flex flex-column">
                                <label className="form-label mb-1">Booking ID / Ref No</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search by Booking ID or Ref No"
                                    value={searchBookingId}
                                    onChange={(e) => setSearchBookingId(e.target.value)}
                                />
                            </div>

                            <div className="d-flex flex-column">
                                <label className="form-label mb-1">Export</label>
                                <div className="d-flex gap-2">
                                    <CSVLink
                                        data={filteredData}
                                        filename="PartialDayCancellation.csv"
                                        className="btn btn-sm btn-outline-primary"
                                        title="Export CSV"
                                    >
                                        <i className="bi bi-file-earmark"></i>
                                    </CSVLink>

                                    <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={exportExcel}
                                        title="Export Excel"
                                    >
                                        <i className="bi bi-file-earmark-excel"></i>
                                    </button>

                                </div>
                            </div>
                        </div>

                        {dateError && (
                            <div className="alert alert-danger py-2">{dateError}</div>
                        )}
                        {error && <div className="alert alert-danger">{error}</div>}

                        <DataTable
                            columns={columns}
                            data={filteredData}
                            pagination
                            paginationPerPage={100}
                            paginationRowsPerPageOptions={[50, 100, 500]}
                            responsive
                            striped
                            highlightOnHover
                            progressPending={pending}
                            customStyles={customStyles}
                            noDataComponent="No partial day cancellations found."
                            fixedHeader
                            fixedHeaderScrollHeight="500px"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartialDayCancellation;
